import { useMemo } from "react";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import {
  PenaltyEnum,
  RestrictionCancelEnum,
  type RestrictionCancel,
} from "@b2bportal/ground-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";
import {
  findRuleWithMatchingDateWindow,
  formatLesserOfFeeOrPercentagePenalty,
} from "../utils/cancellationRules";

export interface UseSteppedCancellationRuleResult {
  copy: string[];
}

/**
 * Generates user-friendly text for stepped cancellation policies in car rentals.
 *
 * Stepped cancellation rules define different penalty amounts depending on WHEN the customer
 * cancels. For example:
 * - Cancel 7+ days before pickup: Free
 * - Cancel 3-7 days before: $50 fee
 * - Cancel 0-3 days before: Non-refundable
 *
 * Special case: When both FlatFee and PercentageTotal apply to the same time window,
 * they are combined into "whichever is lesser" text to protect customers from excessive fees.
 *
 * @param cancellationPolicy - The cancellation policy object from the Ground API
 * @returns An array of user-friendly text strings describing each cancellation rule
 */
export const useSteppedCancellationRule = (
  cancellationPolicy: RestrictionCancel
): UseSteppedCancellationRuleResult => {
  const { formatDateTime, t, formatCurrency, formatNumber } = useI18nContext();
  const copy = useMemo(() => {
    if (
      cancellationPolicy.RestrictionCancel !==
      RestrictionCancelEnum.SteppedCancellationRule
    ) {
      return [];
    }

    // Validate rules array exists before processing
    if (!Array.isArray(cancellationPolicy.rules)) {
      return [];
    }

    // Sort rules chronologically by start date
    const sortedRules = [...cancellationPolicy.rules].sort((a, b) =>
      dayjs(a.from).diff(dayjs(b.from))
    );

    const ruleTexts: string[] = [];
    // Track which rules have been combined with earlier rules to avoid duplicate text
    const combinedIndices = new Set<number>();

    // Generate user-friendly text for each cancellation rule
    sortedRules.forEach((rule, index) => {
      // Skip if this rule was already combined by an earlier rule
      if (combinedIndices.has(index)) {
        return;
      }

      const formattedUntilDate = formatDateTime(
        dayjs(rule.until).toDate(),
        DateTimeFormatStyle.MediumDate
      );

      const penaltyType = rule.penalty.Penalty;
      switch (penaltyType) {
        case PenaltyEnum.Free: {
          const ruleText = t("core-cars.steppedCancellation.free", {
            date: formattedUntilDate,
          });
          ruleTexts.push(ruleText);
          break;
        }

        case PenaltyEnum.NonRefundable: {
          const isLastRule = index === sortedRules.length - 1 && index > 0;

          const ruleText = isLastRule
            ? t("core-cars.steppedCancellation.nonRefundableLast")
            : t("core-cars.steppedCancellation.nonRefundable", {
                date: formattedUntilDate,
              });
          ruleTexts.push(ruleText);
          break;
        }
        case PenaltyEnum.FlatFee: {
          // Check if there's a PercentageTotal penalty for the same date window.
          // If so, combine them into "whichever is lesser" text.
          const matchingPercentageIndex = findRuleWithMatchingDateWindow(
            sortedRules,
            index,
            PenaltyEnum.PercentageTotal,
            rule.from,
            rule.until
          );

          if (matchingPercentageIndex !== -1) {
            // Found a matching PercentageTotal - combine both into one "lesser of" message
            const matchingRule = sortedRules[matchingPercentageIndex];
            if (matchingRule.penalty.Penalty !== PenaltyEnum.PercentageTotal) {
              break; // Type guard for TypeScript exhaustiveness checking
            }

            const combinedText = formatLesserOfFeeOrPercentagePenalty(
              rule.penalty.fee?.amount ?? 0,
              matchingRule.penalty.percentage ?? 0,
              formattedUntilDate,
              formatNumber,
              formatCurrency,
              t
            );

            ruleTexts.push(combinedText);
            // Mark the PercentageTotal rule as already processed so we don't generate duplicate text
            combinedIndices.add(matchingPercentageIndex);
            return;
          }

          // No matching PercentageTotal found - show simple flat fee text
          const formattedAmount = formatCurrency(
            rule.penalty.fee?.amount ?? 0,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }
          );
          const isFirstRule = index === 0;
          const ruleText = isFirstRule
            ? t("core-cars.steppedCancellation.flatFeeFirst", {
                amount: formattedAmount,
                date: formattedUntilDate,
              })
            : t("core-cars.steppedCancellation.flatFee", {
                amount: formattedAmount,
                date: formattedUntilDate,
              });
          ruleTexts.push(ruleText);
          break;
        }

        case PenaltyEnum.NumberOfDays: {
          const ruleText = t("core-cars.steppedCancellation.numberOfDays", {
            days: rule.penalty.num ?? 0,
            date: formattedUntilDate,
          });
          ruleTexts.push(ruleText);
          break;
        }
        case PenaltyEnum.PercentageTotal: {
          const percentage = rule.penalty.percentage ?? 0;

          // Check if there's a FlatFee penalty for the same date window.
          // If so, combine them into "whichever is lesser" text.
          const matchingFlatFeeIndex = findRuleWithMatchingDateWindow(
            sortedRules,
            index,
            PenaltyEnum.FlatFee,
            rule.from,
            rule.until
          );

          if (matchingFlatFeeIndex !== -1) {
            // Found a matching FlatFee - combine both into one "lesser of" message
            const matchingRule = sortedRules[matchingFlatFeeIndex];
            if (matchingRule.penalty.Penalty !== PenaltyEnum.FlatFee) {
              break; // Type guard for TypeScript exhaustiveness checking
            }

            const combinedText = formatLesserOfFeeOrPercentagePenalty(
              matchingRule.penalty.fee?.amount ?? 0,
              percentage,
              formattedUntilDate,
              formatNumber,
              formatCurrency,
              t
            );

            ruleTexts.push(combinedText);
            // Mark the FlatFee rule as already processed so we don't generate duplicate text
            combinedIndices.add(matchingFlatFeeIndex);
            return;
          }

          // No matching FlatFee found - show percentage-based text
          // Special cases: 0% = free, 100% = non-refundable
          if (percentage === 0) {
            const ruleText = t("core-cars.steppedCancellation.free", {
              date: formattedUntilDate,
            });
            ruleTexts.push(ruleText);
          } else if (percentage === 100) {
            const ruleText = t("core-cars.steppedCancellation.nonRefundable", {
              date: formattedUntilDate,
            });
            ruleTexts.push(ruleText);
          } else {
            const formattedPercentage = formatNumber(percentage / 100, {
              style: "percent",
            });

            const ruleText = t(
              "core-cars.steppedCancellation.percentageTotal",
              {
                percentage: formattedPercentage,
                date: formattedUntilDate,
              }
            );
            ruleTexts.push(ruleText);
          }
          break;
        }
        default:
          ensureExhaustive(penaltyType);
      }
    });

    return ruleTexts;
  }, [cancellationPolicy, formatCurrency, formatDateTime, formatNumber, t]);

  return { copy };
};
